import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  education,
  getTypeColor,
  getTypeLabel,
  getStatusColor,
  getStatusLabel,
} from "../data/education";

const Education: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const,
      },
    },
  };

  return (
    <section
      id="education"
      className="py-20 bg-gradient-to-br from-secondary-50 via-white to-blue-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-blue-900/20 relative overflow-hidden"
    >
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-10 right-10 w-32 h-32 bg-primary-200 dark:bg-primary-600 rounded-full opacity-20 animate-float"></div>
        <div
          className="absolute bottom-10 left-10 w-40 h-40 bg-blue-200 dark:bg-blue-600 rounded-full opacity-20 animate-float"
          style={{ animationDelay: "2s" }}
        ></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="max-w-6xl mx-auto"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 bg-gradient-to-r from-gray-900 via-primary-600 to-blue-600 dark:from-white dark:via-primary-400 dark:to-blue-400 bg-clip-text text-transparent">
              Formation
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-primary-600 to-blue-600 dark:from-primary-400 dark:to-blue-400 mx-auto rounded-full mb-8"></div>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Mon parcours académique
            </p>
          </motion.div>

          {/* Timeline */}
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary-500 to-blue-500"></div>

            <div className="space-y-12">
              {education.map((item, index) => (
                <motion.div
                  key={item.id}
                  variants={itemVariants}
                  className={`relative flex items-center ${
                    index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                >
                  {/* Timeline dot */}
                  <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 w-4 h-4 bg-primary-500 rounded-full border-4 border-white dark:border-secondary-900 shadow-soft"></div>

                  {/* Content */}
                  <div
                    className={`ml-12 md:ml-0 md:w-5/12 ${
                      index % 2 === 0 ? "md:pr-8" : "md:pl-8"
                    }`}
                  >
                    <motion.div
                      whileHover={{ scale: 1.02, y: -2 }}
                      className="bg-white/80 dark:bg-secondary-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-soft dark:shadow-dark-soft border border-white/20 dark:border-secondary-700/20 hover:border-primary-200 dark:hover:border-primary-600/50 transition-all duration-300"
                    >
                      {/* Type and status badges */}
                      <div className="flex items-center gap-2 mb-4">
                        <span
                          className={`px-3 py-1 text-xs font-semibold rounded-full ${getTypeColor(
                            item.type
                          )}`}
                        >
                          {getTypeLabel(item.type)}
                        </span>
                        {item.status && (
                          <span
                            className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                              item.status
                            )}`}
                          >
                            {getStatusLabel(item.status)}
                          </span>
                        )}
                      </div>

                      {/* Degree and school */}
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        {item.degree}
                      </h3>
                      <div className="flex items-center gap-2 mb-4">
                        <span className="text-lg font-semibold text-primary-600 dark:text-primary-400">
                          {item.school}
                        </span>
                        {item.location && (
                          <>
                            <span className="text-gray-400">•</span>
                            <span className="text-gray-600 dark:text-gray-400">
                              {item.location}
                            </span>
                          </>
                        )}
                      </div>

                      {/* Period */}
                      <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        {item.period}
                      </div>

                      {/* Description */}
                      <p className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                        {item.description}
                      </p>

                      {/* Specializations */}
                      {item.specializations &&
                        item.specializations.length > 0 && (
                          <div className="mb-4">
                            <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-2">
                              Spécialisations :
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {item.specializations.map((spec, specIndex) => (
                                <span
                                  key={specIndex}
                                  className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full border border-blue-200 dark:border-blue-700/30"
                                >
                                  {spec}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                      {/* Achievements */}
                      {item.achievements && item.achievements.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="font-semibold text-gray-900 dark:text-white text-sm">
                            Réalisations :
                          </h4>
                          <ul className="space-y-1">
                            {item.achievements.map(
                              (achievement, achievementIndex) => (
                                <li
                                  key={achievementIndex}
                                  className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2"
                                >
                                  <span className="text-primary-500 mt-1">
                                    •
                                  </span>
                                  {achievement}
                                </li>
                              )
                            )}
                          </ul>
                        </div>
                      )}

                      {/* GPA */}
                      {item.gpa && (
                        <div className="mt-4 p-3 bg-gray-50 dark:bg-secondary-700/50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              GPA
                            </span>
                            <span className="text-lg font-bold text-primary-600 dark:text-primary-400">
                              {item.gpa}
                            </span>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Call to action */}
        </motion.div>
      </div>
    </section>
  );
};

export default Education;
